import { FC } from 'react';

import { Checkbox, FormControlLabel } from '@mui/material';
import { useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import { Button, Input } from 'view/components';
import SalaryRange from 'view/components/SalaryRange';
import * as S from 'view/modals/OnboardingWizard/styled';
import { palette } from 'view/themes/palette';
import IndustryDropdownAutocomplete from 'view/pages/profile/components/IndustryDropdownAutocomplete';
import JobCategoryDropdownAutocomplete from 'view/pages/profile/components/JobCategoryDropdownAutocomplete';
import JobLocationAutocomplete from 'view/pages/profile/components/JobLocationAutocomplete';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import SearchableMultiplySelect from 'view/pages/profile/TestPage/SearchableMultiplySelect';

interface JobPreferencesStepProps {
  onBack: () => void;
  onNext: () => void;
  isSaving?: boolean;
}

const JobPreferencesStep: FC<JobPreferencesStepProps> = ({ onBack, onNext, isSaving = false }) => {
  const { watch, setValue, control } = useFormContext();

  const industryValue = watch('industry') ?? null;
  const jobCategoryValue = watch('jobCategory') ?? null;
  const jobTitleValue = watch('jobTitle') ?? '';
  const salaryRange = watch('salaryRange') ?? { from: '', to: '' };
  const jobLocationValue = watch('jobLocation') ?? null;
  const onSiteValue = watch('onSite') ?? false;
  const remoteValue = watch('remote') ?? false;
  const hybridValue = watch('hybrid') ?? false;
  const qualificationsValue = watch('qualifications') ?? null;
  const skillsValue = watch('skills') ?? null;

  const { data: qualificationData, isLoading: isQualificationLoading } = useGetDropdownData(
    DropdownsEnum.QUALIFICATION
  );
  const { data: skillsData, isLoading: isSkillsLoading } = useGetDropdownData(DropdownsEnum.SKILL);

  // All fields are mandatory
  const hasLocation = jobLocationValue || onSiteValue || remoteValue || hybridValue;
  const isValid =
    industryValue &&
    jobCategoryValue &&
    jobTitleValue &&
    salaryRange?.from &&
    salaryRange?.to &&
    hasLocation &&
    qualificationsValue?.length > 0 &&
    skillsValue?.length > 0;

  return (
    <S.WizardContent>
      <S.StepHeader>
        <S.StepTitle>Job Preferences</S.StepTitle>
        <S.StepSubtitle
          style={{
            width: 374,
          }}
        >
          Your profile highlights your skills, experience, and potential to match you with the right
          opportunities
        </S.StepSubtitle>
      </S.StepHeader>
      <S.StepFormContainerTight>
        <IndustryDropdownAutocomplete
          industryValue={industryValue}
          setValue={setValue}
          label="Industry"
        />
        <JobCategoryDropdownAutocomplete
          jobCategoryValue={jobCategoryValue}
          setValue={setValue}
          label="Job Category"
        />
        <Input
          name="jobTitle"
          label="Job Title / Role (Desired)"
          value={jobTitleValue}
          marginbottom={0}
          placeholder="Enter your desired job title"
        />
        <SalaryRange
          fromValue={salaryRange.from}
          toValue={salaryRange.to}
          onChange={(field, value) => {
            return setValue('salaryRange', { ...salaryRange, [field]: value });
          }}
          label="Salary Range"
        />
        <S.FormSection>
          <S.FormSectionLabel
            style={{
              fontSize: 16,
            }}
          >
            Location
          </S.FormSectionLabel>
          <JobLocationAutocomplete
            setValue={setValue}
            jobLocationValue={jobLocationValue}
            marginbottom={0}
            label=""
          />
          <S.LocationTypeContainer>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onSiteValue}
                  onChange={(e) => {
                    return setValue('onSite', e.target.checked);
                  }}
                  sx={{ '&.Mui-checked': { color: palette.alerts.success } }}
                />
              }
              label="On-Site"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' }, margin: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={remoteValue}
                  onChange={(e) => {
                    return setValue('remote', e.target.checked);
                  }}
                  sx={{ '&.Mui-checked': { color: palette.alerts.success } }}
                />
              }
              label="Remote"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' }, margin: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hybridValue}
                  onChange={(e) => {
                    return setValue('hybrid', e.target.checked);
                  }}
                  sx={{ '&.Mui-checked': { color: palette.alerts.success } }}
                />
              }
              label="Hybrid"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' }, margin: 0 }}
            />
          </S.LocationTypeContainer>
        </S.FormSection>
        <SearchableMultiplySelect
          name="qualifications"
          label="Qualifications"
          options={qualificationData || []}
          control={control}
          disabled={isQualificationLoading}
        />
        <SearchableMultiplySelect
          name="skills"
          label="Skills"
          options={skillsData || []}
          control={control}
          disabled={isSkillsLoading}
        />
      </S.StepFormContainerTight>
      <S.WizardFooter>
        <Button variant="outlined" text="Back" onClick={onBack} disabled={isSaving} />
        <Button
          variant="contained"
          text={isSaving ? 'Saving...' : 'Next'}
          onClick={onNext}
          disabled={!isValid || isSaving}
          endIcon={<ArrowIcon rotate="right" color="#FFFFFF" />}
        />
      </S.WizardFooter>
    </S.WizardContent>
  );
};

export default JobPreferencesStep;
